/**
 * Configuration for table-aware SYSTEM log formatting.
 *
 * Maps database table names to human-readable labels, row identifier fields,
 * field display labels, and log type classification. Used by getLogText to
 * dispatch SYSTEM logs from new client-background tables to the correct
 * text formatter.
 */

export interface TableLogConfig {
  /** Human-readable table name for log sentences */
  tableLabel: string;
  /** Column used to identify specific rows (e.g. "school_name") */
  rowIdentifierField?: string;
  /** column_name → display label mapping */
  fields: Record<string, string>;
  /** Controls formatting: standard = single-row upsert, table_entry = multi-row CRUD,
   *  external_provider = provider-specific format */
  type: "standard" | "table_entry" | "external_provider";
}

/** Enum display maps keyed by field name. Used by transformEnumValue. */
export const ENUM_DISPLAY_MAPS: Record<string, Record<string, string>> = {
  facility_type: {
    SCHOOL: "school",
    DAYCARE: "daycare",
    HOMESCHOOL: "homeschool",
    VIRTUAL_SCHOOL: "virtual school",
    NOT_YET: "not yet enrolled",
  },
  educational_placement: {
    FULLY_INTEGRATED: "fully integrated",
    PARTIALLY_INTEGRATED: "partially integrated",
    SELF_CONTAINED: "self contained",
    HOME_BASED: "home based",
  },
  child_temperament: {
    EASYGOING: "easygoing",
    IRRITABLE: "irritable",
  },
  degree_of_independence: {
    INDEPENDENT: "independent",
    PARTIALLY_SUPPORTED: "partially supported",
    FULLY_SUPPORTED: "fully supported",
  },
  medication_adherence: {
    NOT_PRESCRIBED: "not prescribed",
    REGULARLY_MISSES_DOSES: "regularly misses doses",
    OCCASIONALLY_MISSES_DOSES: "occasionally misses doses",
    TAKES_AS_PRESCRIBED: "takes as prescribed",
  },
  dosage_unit: {
    ML: "mL",
    MG: "mg",
    MCG: "mcg",
    G: "g",
    UNITS: "units",
    PUFFS: "puffs",
  },
  frequency_interval: {
    DAYS: "days",
    WEEKS: "weeks",
    MONTHS: "months",
  },
  collaboration_schedule_interval: {
    DAYS: "days",
    WEEKS: "weeks",
    MONTHS: "months",
  },
  feeling_down: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
    NOT_APPLICABLE: "not applicable",
  },
  little_interest: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
    NOT_APPLICABLE: "not applicable",
  },
  has_trauma_history: {
    YES: "yes",
    NO: "no",
  },
  interested_in_being_held: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
  },
  interested_in_being_played_with: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
  },
  caregiver_attended_to_needs: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
  },
  has_received_services: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
  },
  ongoing: {
    YES: "yes",
    NO: "no",
    UNKNOWN: "unknown",
  },
  sleeps_through_night: {
    YES: "yes",
    NO: "no",
  },
  takes_naps_regularly: {
    YES: "yes",
    NO: "no",
  },
  wakes_and_gets_out: {
    YES: "yes",
    NO: "no",
  },
  wakes_and_seeks_adult: {
    YES: "yes",
    NO: "no",
  },
  evaluation_comorbid_diagnoses: {
    YES: "yes",
    NO: "no",
    "N/A": "n/a",
  },
};

export const SYSTEM_LOG_TABLE_CONFIG: Record<string, TableLogConfig> = {
  // Section 3: Education
  education_history: {
    tableLabel: "Education",
    rowIdentifierField: "school_name",
    type: "table_entry",
    fields: {
      school_name: "school name",
      grade: "grade",
      facility_type: "facility type",
      educational_placement: "educational placement",
      days_of_week: "days of week",
      start_time: "start time",
      end_time: "end time",
      hours: "hours per week",
    },
  },

  // Section 4: Criminal History
  drug_criminal_history: {
    tableLabel: "Criminal History",
    type: "standard",
    fields: {
      drug_alcohol_use: "drug or alcohol use",
      criminal_legal_history: "criminal and legal history",
    },
  },

  // Section 5: Developmental History
  developmental_history: {
    tableLabel: "Developmental History",
    type: "standard",
    fields: {
      when_development_seemed_different: "when development started to seem different",
      complications_pregnancy_birth: "complications during pregnancy/birth",
      age_began_crawling_months: "age began crawling (months)",
      age_began_walking_months: "age began walking (months)",
      age_began_speaking_months: "age began speaking (months)",
      child_temperament: "child temperament",
      interested_in_being_held: "interested in being held",
      interested_in_being_played_with: "interested in being played with",
      caregiver_attended_to_needs: "caregiver attended to needs",
      additional_notes_infancy_childhood: "additional notes on infancy/childhood",
    },
  },

  // Section 6: Family History
  family_history: {
    tableLabel: "Family History",
    type: "standard",
    fields: {
      caregiver_main_concerns: "caregiver main concerns",
      daily_routine_structure: "daily routine and structure",
      family_caregivers_in_home: "family/caregivers in home",
    },
  },

  // Section 7: Family Medical History
  family_medical_history: {
    tableLabel: "Family Medical History",
    rowIdentifierField: "family_member",
    type: "table_entry",
    fields: {
      family_member: "family member name",
      relationship: "relation to client",
      diagnoses: "diagnosis",
      current_treatments: "current treatments",
      past_treatments: "past treatments",
    },
  },

  // Section 8: Family Mental Health History
  family_mental_health_history: {
    tableLabel: "Family Mental Health History",
    rowIdentifierField: "family_member",
    type: "table_entry",
    fields: {
      family_member: "family member name",
      relationship: "relation to client",
      diagnoses: "diagnoses",
      current_support_services: "current support services",
      past_support_services: "past support services",
    },
  },

  // Section 9: Intervention History
  intervention_history: {
    tableLabel: "Intervention History",
    rowIdentifierField: "service_provided",
    type: "table_entry",
    fields: {
      start_date: "start date",
      end_date: "end date",
      service_provided: "services provided",
      ongoing: "services ongoing",
      perceived_effectiveness: "perceived effectiveness",
    },
  },

  // Section 10: Medical History
  medical_history: {
    tableLabel: "Medical History",
    type: "standard",
    fields: {
      emergency_room_visits: "emergency room visits",
      vision_screen_interventions: "vision screen/interventions",
      fall_risk: "fall risk",
      last_dental_exam: "last dental exam",
      last_well_visit: "last well-visit with PCP",
    },
  },

  // Section 10b: Allergies (child of Medical History)
  allergies: {
    tableLabel: "Allergies",
    rowIdentifierField: "allergy_type",
    type: "table_entry",
    fields: {
      allergy_type: "allergy type",
    },
  },

  // Section 11: Medications
  medication: {
    tableLabel: "Medications",
    rowIdentifierField: "name",
    type: "table_entry",
    fields: {
      name: "medication name",
      dose_value: "dose value",
      dose_unit: "dose unit",
      frequency_value: "frequency value",
      frequency_interval: "frequency interval",
      prescribing_doctor: "prescribing provider",
      collaboration_schedule_interval: "collaboration schedule",
    },
  },

  // Section 12: Sleep Profile
  sleep_profile: {
    tableLabel: "Sleep Profile",
    type: "standard",
    fields: {
      notable_sleep_concerns: "notable sleep concerns",
      bedtime_routine: "bedtime routine",
      sleeps_through_night: "sleeps through the night",
      takes_naps_regularly: "takes naps regularly",
      wakes_and_gets_out: "wakes and gets out of bed",
      wakes_and_seeks_adult: "wakes and seeks adult",
    },
  },

  // Section 13: Social Activities
  social_history: {
    tableLabel: "Social Activities",
    rowIdentifierField: "activity_type",
    type: "table_entry",
    fields: {
      activity_type: "activity type",
      start_date: "start date",
      end_date: "end date",
      degree_of_independence: "degree of independence",
    },
  },

  // Section 14: Trauma History
  trauma_history: {
    tableLabel: "Trauma History",
    type: "standard",
    fields: {
      has_trauma_history: "history of trauma",
    },
  },

  // Section 15: Treatment Variables
  variables_impacting_treatment: {
    tableLabel: "Treatment Variables",
    type: "standard",
    fields: {
      spiritual_factors: "spiritual factors",
      cultural_factors: "cultural factors",
      legal_factors: "legal factors",
      linguistic_factors: "linguistic factors",
      community_resources: "community resources",
    },
  },

  // Section 16: Depression Screening
  depression_screening: {
    tableLabel: "Depression Screening",
    type: "standard",
    fields: {
      feeling_down: "feeling down, depressed, or hopeless",
      little_interest: "little interest or pleasure in doing things",
    },
  },

  // Section 17: Financial Guarantor
  insurance_guarantor: {
    tableLabel: "Financial Guarantor",
    type: "standard",
    fields: {
      first_name: "guarantor's first name",
      last_name: "guarantor's last name",
      date_of_birth: "guarantor's date of birth",
      gender: "guarantor's gender",
      email: "guarantor's email",
      phone: "guarantor's phone number",
      relationship_to_client: "relationship to client",
      country: "country",
      state: "state",
      city: "city",
      zip_code: "ZIP code",
      address_1: "address",
      address_2: "address 2",
    },
  },

  // Section 18: Care Team
  team_members: {
    tableLabel: "Care Team",
    rowIdentifierField: "first_name",
    type: "table_entry",
    fields: {
      relationship_type: "relationship to client",
      legal_guardian: "legal guardian",
      document_signer: "designated caregiver",
      first_name: "first name",
      last_name: "last name",
      date_of_birth: "date of birth",
      gender: "gender",
      phone_number: "phone number",
      email: "email address",
      address1: "address",
      address2: "address 2",
      country: "country",
      state: "state",
      city: "city",
      zip_code: "ZIP code",
      timezone: "time zone",
    },
  },

  // Section 19: Client Availability
  client_availability: {
    tableLabel: "Client Availability",
    type: "standard",
    fields: {
      make_up_day: "make-up day",
      make_up_time: "make-up time",
    },
  },

  // Section 20: Therapy Hours
  client_therapy_hours: {
    tableLabel: "Therapy Hours",
    type: "standard",
    fields: {
      assessment_hours: "assessment hours",
      direct_therapy_hours: "direct therapy hours",
      caregiver_mediated_hours: "caregiver mediated hours",
      protocol_modification_hours: "direct protocol modification hours",
      indirect_protocol_modification_hours: "indirect protocol modification hours",
      caregiver_training_hours: "caregiver training hours",
    },
  },

  // Section 21: Providers
  client_providers: {
    tableLabel: "Providers",
    rowIdentifierField: "last_name",
    type: "external_provider",
    fields: {
      type: "provider type",
      first_name: "first name",
      last_name: "last name",
      phone: "contact information",
      physical_address: "physical address",
      city: "city",
      state: "state",
      zip_code: "zip code",
      is_referring_provider: "referring provider",
    },
  },

  // Client Address (tracked via audit trigger, not a numbered section)
  client_address: {
    tableLabel: "Client Address",
    type: "standard",
    fields: {
      street_1: "address",
      street_2: "address 2",
      city: "city",
      state: "state",
      zip_code: "ZIP code",
      country: "country",
      timezone: "time zone",
    },
  },
};
